import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, switchMap, take, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiResponse } from '../utils/api-response';
import { Product, ProductCategory } from './product-shared.types';

@Injectable({
    providedIn: 'root',
})
export class ProductSharedervice {
    private readonly BASE_URL = environment.apiUrl;
    protected readonly PRODUCT_URL = `${this.BASE_URL}/products`;
    protected readonly PRODUCT_CATEGORIES_URL = `${this.BASE_URL}/product-categories`;

    // BehaviorSubjects
    protected _category: BehaviorSubject<ProductCategory | null> =
        new BehaviorSubject<ProductCategory | null>(null);
    protected _categories: BehaviorSubject<ProductCategory[] | null> =
        new BehaviorSubject<ProductCategory[] | null>(null);

    protected _product: BehaviorSubject<Product | null> =
        new BehaviorSubject<Product | null>(null);
    protected _products: BehaviorSubject<Product[] | null> = new BehaviorSubject<
        Product[] | null
    >(null);

    /**
     * Constructor
     */
    constructor(protected _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for Product
     */
    get product$(): Observable<Product | null> {
        return this._product.asObservable();
    }

    /**
     * Getter for Product
     */
    get products$(): Observable<Product[] | null> {
        return this._products.asObservable();
    }

    // Accessors
    get category$(): Observable<ProductCategory | null> {
        return this._category.asObservable();
    }

    get categories$(): Observable<ProductCategory[] | null> {
        return this._categories.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Fetch all products by school
     */
    fetchProductList(schoolCode: string): Observable<ApiResponse<Product[]>> {
        return this._httpClient
            .get<ApiResponse<Product[]>>(
                `${this.PRODUCT_URL}/school/${schoolCode}`
            )
            .pipe(
                tap((response) => {
                    // Make an HTTP GET request to fetch products based on the school code
                    if (response.success) {
                        // console.log(
                        //     'Product list fetched successfully:',
                        //     response.data
                        // );
                    }
                }),
                // Log a success message if the response is successful
                catchError((error) => {
                    console.error('Failed to fetch product list:', error);
                    const errorResponse: ApiResponse<Product[]> =
                        error?.error || {
                            success: false,
                            message: 'Failed to fetch product list',
                            // Log an error message if the request fails
                            data: null,

                            // Create an error response with a default message and return it
                            timestamp: new Date(),
                        };
                    return throwError(() => errorResponse);
                })
            );
    }

    // Category Methods
    getCategoriesBySchool(
        schoolCode: string
    ): Observable<ApiResponse<ProductCategory[]>> {
        // console.log(
        //     'Fetching product categories for school code:',
        //     schoolCode
        // );
        return this._httpClient
            .get<ApiResponse<ProductCategory[]>>(
                `${this.PRODUCT_CATEGORIES_URL}/school/${schoolCode}`
            )
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        this._categories.next(response.data);
                    }
                }),
                catchError((error) => {
                    console.error('Failed to fetch product categories:', error);
                    const errorResponse: ApiResponse<ProductCategory[]> =
                        error?.error || {
                            success: false,
                            message: 'Failed to fetch product categories',
                            data: null,
                            timestamp: new Date(),
                        };
                    return throwError(() => errorResponse);
                })
            );
    }

    
    /**
     * Get product categories by school
     */
    getCategories(schoolCode: string): Observable<{
        success: boolean;
        message: string;
        data: ProductCategory[];
        timestamp: string;
    }> {
        return this._httpClient.get<{
            success: boolean;
            message: string;
            data: ProductCategory[];
            timestamp: string;
        }>(`${this.BASE_URL}/product-categories/school/${schoolCode}`);
    }


    getPagedCategoriesBySchoolAndStatus(
        schoolCode: string,
        status: string,
        page: number = 0,
        size: number = 10
      ): Observable<ApiResponse<{content: ProductCategory[]; totalElements: number}>> {
        const url = `${this.PRODUCT_CATEGORIES_URL}/${schoolCode}/status/${status}/paged?page=${page}&size=${size}`;
      
        return this._httpClient
          .get<ApiResponse<{content: ProductCategory[]; totalElements: number}>>(url)
          .pipe(
            tap((response) => {
            //   console.log('Fetched paged categories:', response);
            }),
            catchError((error) => {
              console.error('Failed to fetch paged categories:', error);
              const errorResponse: ApiResponse<{content: ProductCategory[]; totalElements: number}> = error?.error || {
                success: false,
                message: 'Failed to fetch paged categories',
                data: null,
                timestamp: new Date(),
              };
              return throwError(() => errorResponse);
            })
          );
      }
}
